import { translate, translateRich, useLocale } from "../localization";
// Leaderboard.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from "recharts";
import InventoryAPI from "../services/InventoryAPI"; // <-- adjust path if needed

// Current poll options use the content IDs recorded by the voting API.
const EXPERIENCE_ITEMS = () => [{
  id: "1a5f16cd-cd09-4a84-9d53-b54537f672dd",
  label: translate("Heart Attack!")
}, {
  id: "45b71015-5dfd-49e5-8215-b183ca614dd5",
  label: translate("Anatomy of a Data Center")
}, {
  id: "157d0c9d-25a5-4e05-b2ff-c75ce3c44e5b",
  label: translate("A Tale of Two Grasses")
}, {
  id: "039398f4-d316-4a32-b63f-53a8fc775a8d",
  label: translate("Organelles R Us")
}];

const Leaderboard = () => {
  const locale = useLocale();
  const [voteData, setVoteData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    const loadVotes = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const responses = await Promise.all(
          EXPERIENCE_ITEMS().map(async (experienceItem) => {
            const response = await InventoryAPI.get(`/votes/${experienceItem.id}`, {
              requiresAuth: true,
            });

            // Adjust these keys to match your API response:
            const voteCount = response.data.data?.totalCoins ?? response.data?.votes ?? response.data?.count ?? 0;

            return {
              id: experienceItem.id,
              label: experienceItem.label,
              votes: voteCount,
            };
          })
        );

        if (!isCancelled) {
          const sortedData = [...responses].sort((firstItem, secondItem) => secondItem.votes - firstItem.votes);
          setVoteData(sortedData);
        }
      } catch (error) {
        console.error("Error loading votes:", error);
        if (!isCancelled) {
          setLoadError(translate("Something went wrong while loading leaderboard data."));
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadVotes();

    return () => {
      isCancelled = true;
    };
  }, [locale]);

  const totalVotes = voteData.reduce((sum, experienceItem) => sum + experienceItem.votes, 0);
  const maxVotes = voteData.reduce(
    (maxValue, experienceItem) => (experienceItem.votes > maxValue ? experienceItem.votes : maxValue),
    0
  );
  const topExperience = voteData[0];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "var(--color-bg-surface, var(--color-bg-page, #f4f4f4))",
        backgroundAttachment: "fixed",
        backgroundPosition: "center top",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "var(--color-bg-card, #ffffff)",
          borderBottom: "1px solid var(--color-border-subtle, rgba(0,0,0,0.12))",
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h5" fontWeight={600}>{translate("Leaderboard")}</Typography>
          <Typography variant="body2" color="text.secondary">{translate("Poll results from your 10k Science experiences")}</Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3, flexGrow: 1 }}>
        {isLoading ? (
          <Box
            sx={{
              height: "50vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <Stack spacing={2}>
            {/* Summary strip for screenshots */}
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderRadius: 0,
                border: "1px solid var(--color-border-subtle, rgba(0,0,0,0.12))",
                backgroundColor: "var(--color-bg-card, #ffffff)",
              }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">{translate("Total votes")}</Typography>
                <Typography variant="h4" fontWeight={700}>
                  {totalVotes}
                </Typography>
              </Box>

              {topExperience && (
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="subtitle2" color="text.secondary">{translate("Top experience")}</Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {topExperience.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">{translateRich("{value0} votes", {
  value0: topExperience.votes
})}</Typography>
                </Box>
              )}
            </Paper>

            {/* Poll-style results */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 0,
                border: "1px solid var(--color-border-subtle, rgba(0,0,0,0.12))",
                backgroundColor: "var(--color-bg-card, #ffffff)",
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{translate("Poll results")}</Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1.5}>
                {voteData.map((experienceItem, index) => {
                  const percentageOfMax = maxVotes > 0 ? (experienceItem.votes / maxVotes) * 100 : 0;
                  const percentageOfTotal = totalVotes > 0 ? (experienceItem.votes / totalVotes) * 100 : 0;

                  const isLeader = index === 0;

                  return (
                    <Box
                      key={experienceItem.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          textAlign: "center",
                          fontWeight: 600,
                          color: isLeader ? "var(--color-action-success, #53be77)" : "inherit",
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={isLeader ? 700 : 500} sx={{ mb: 0.5 }}>
                          {experienceItem.label}
                        </Typography>

                        <Box
                          sx={{
                            height: 20,
                            backgroundColor: "var(--color-bg-highlight, #dddddd)",
                            borderRadius: 0,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${percentageOfMax}%`,
                              background:
                                "linear-gradient(90deg, var(--color-action-primary, #239fc6), var(--color-action-primary-hover, #296f85))",
                              borderRadius: 0,
                              transition: "width 0.4s ease",
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ textAlign: "right", minWidth: 72 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {experienceItem.votes}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                          {Math.round(percentageOfTotal)}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default Leaderboard;
